import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Button, Chip, CircularProgress, Divider, ListItemIcon, ListItemText,
  Menu, MenuItem, Tooltip, Typography,
} from '@mui/material';
import {
  LocalHospital as HospitalIcon,
  MedicalServices as ClinicIcon,
  ExpandMore as ExpandIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../api/branding';
import { onSitesChanged } from '../utils/sitesChanged';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Which clinic or hospital you are currently working in, shown at the top of every
 * screen.
 *
 * Two shapes, decided by how many offices you actually have:
 *
 *  - ONE office (every account before multi-site existed): a plain label. No menu, no
 *    fetch cost paid for a choice that doesn't exist.
 *  - SEVERAL: the same label becomes a picker. Choosing one calls /auth/switch-clinic,
 *    which re-mints the session for that site and returns it directly — both sites are
 *    the same portal on the same origin, so there is no handoff redirect to sit through.
 *
 * The label renders immediately from the token (`practice_name`), so the header never
 * flashes empty; the site list arrives afterwards and only decides whether it becomes
 * clickable.
 */
export default function SiteContext() {
  const { user, applySession, isPlatformEntry } = useAuth();
  const [sites, setSites] = useState([]);
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);

  const role = user?.role;
  const isOrgAdmin = role === 'org_admin';
  // Enterprise and region admins sit above any one site, so they are titled by the
  // level they run — until this existed their header had no left-hand side at all.
  const isNodeAdmin = ['enterprise_admin', 'region_admin'].includes(role);
  const orgType = user?.org_type || 'clinic';
  const isHospital = orgType === 'hospital';

  // An org admin's header names the organisation; everyone else's names their site.
  const label = isOrgAdmin
    ? (user?.org_name || 'Organisation')
    : isNodeAdmin ? (user?.node_name || '')
    : (user?.practice_name || '');
  // The customer's own logo, when their account (or a level above it) has one.
  const { shown } = useBranding(role !== 'super_admin' && role !== 'patient');
  const logo = shown?.logo;

  const canSwitch = !isPlatformEntry && !isOrgAdmin && !isNodeAdmin
    && !['super_admin', 'patient'].includes(role);
  // An org admin can't switch sites from here, but still needs the live site count:
  // org_site_count rides on the token, so it is stale the moment a site is added.
  const wantsSites = canSwitch || isOrgAdmin;

  useEffect(() => {
    if (!wantsSites) return undefined;
    let alive = true;
    const fetchSites = () => axios
      .get(`${API_URL}/api/v1/auth/my-sites`)
      .then(({ data }) => { if (alive) setSites(data?.sites || []); })
      .catch(() => {});
    fetchSites();
    const stop = onSitesChanged(fetchSites);
    return () => { alive = false; stop(); };
  }, [wantsSites, user?.clinic_id]);

  const go = useCallback(async (site) => {
    if (site.clinic_id === user?.clinic_id) { setAnchor(null); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/v1/auth/switch-clinic`, {
        clinic_id: site.clinic_id,
      });
      applySession(data.user);
      // A full reload rather than a re-render: every open screen is holding data for
      // the site we just left — patient lists, the queue, a half-loaded dashboard —
      // and re-fetching them piecemeal is how stale rows from another office end up
      // on screen.
      window.location.assign('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not switch site');
      setBusy(false);
      setAnchor(null);
    }
  }, [applySession, user?.clinic_id]);

  // Prefer what the server just told us over the token's snapshot.
  const siteCount = sites.length || user?.org_site_count || 0;

  if (!label) return null;

  const Icon = isHospital ? HospitalIcon : ClinicIcon;
  const multi = canSwitch && sites.length > 1;

  const inner = (
    <>
      {busy
        ? <CircularProgress size={17} sx={{ color: 'primary.main' }} />
        : logo
          ? (
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{ height: 36, width: 'auto', maxWidth: { xs: 72, sm: 132 }, objectFit: 'contain', display: 'block' }}
            />
          )
          : <Icon sx={{ fontSize: 20, color: 'primary.main' }} />}
      {logo && <Box sx={{ width: '1px', alignSelf: 'stretch', my: 0.5, bgcolor: 'rgba(4, 47, 46, 0.12)' }} />}
      <Box sx={{ minWidth: 0, textAlign: 'left' }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          noWrap
          sx={{ lineHeight: 1.2, color: 'text.primary', maxWidth: { xs: 140, sm: 280 } }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', lineHeight: 1.1 }}
          >
            {isOrgAdmin
              ? `${siteCount} site${siteCount === 1 ? '' : 's'}`
              : isNodeAdmin ? (role === 'enterprise_admin' ? 'Enterprise' : 'Region')
              : (isHospital ? 'Hospital' : 'Clinic')}
          </Typography>
          {/* Says, at a glance and on every screen, that this is the practice the
              organisation's other sites were opened under — the same label the Sites
              roster and the platform clinic list use. An org admin's header names the
              organisation itself, so it would be meaningless there. */}
          {!isOrgAdmin && !isNodeAdmin && user?.org_is_top_site && (
            <Chip
              label="Main site"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }}
            />
          )}
        </Box>
      </Box>
      {multi && <ExpandIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
    </>
  );

  if (!multi) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, px: 0.5 }}>
        {inner}
      </Box>
    );
  }

  return (
    <>
      <Tooltip title="Switch site">
        <Button
          onClick={(e) => setAnchor(e.currentTarget)}
          disabled={busy}
          sx={{
            textTransform: 'none', gap: 1.25, minWidth: 0, px: 1, py: 0.5,
            borderRadius: 2, color: 'text.primary',
            '&:hover': { bgcolor: 'rgba(13,148,136,0.08)' },
          }}
        >
          {inner}
        </Button>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <Typography
          variant="caption"
          sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block' }}
        >
          {sites[0]?.organization_name || 'Your sites'}
        </Typography>
        <Divider />
        {sites.map((s) => {
          const active = s.clinic_id === user?.clinic_id;
          return (
            <MenuItem key={s.clinic_id} selected={active} onClick={() => go(s)}>
              <ListItemIcon>{active ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
              <ListItemText
                primary={s.name}
                secondary={s.type === 'hospital' ? 'Hospital' : 'Clinic'}
              />
              {active && <Chip label="Current" size="small" sx={{ ml: 1.5 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
